import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSuccessionTheme } from '../SuccessionThemeProvider';
import numeral from 'numeral';
import { currencySymbol } from '../../../utils/formatters';

// Logical step indices — these are the `case` numbers in SuccessionPartialSalesWizard's
// getStepContent, NOT positions in the rendered stepper. `goToStep` converts between the two,
// so these stay correct whether or not the Price Details step is filtered out.
//
// Named rather than inlined on purpose: every one of these was wrong as a bare integer, each
// Edit button silently navigating to a different step than the heading beside it named.
const STEP = {
  FINANCIAL_DETAILS: 0,
  DETAILS: 1,
  PRICE_DETAILS: 2,
  DESCRIPTION: 3,
};

const Review = ({
  formData,
  company,
  partialSaleQuestions,
  partialSalesPropertyCasualty,
  hideProductSalesPurchasePrice,
  goToStep
}) => {
  const intl = useIntl();
  const { theme, isLightTheme, isDarkTheme } = useSuccessionTheme();

  // Edit button component
  const EditButton = ({ onClick, label }) => (
    <button
      onClick={onClick}
      className="ml-2 p-1 text-succession-primary hover:text-succession-primary-hover transition-colors rounded hover:bg-succession-bg-secondary"
      title={intl.formatMessage({ id: 'succession.partial_sales.review.edit_label' }, { label })}
      type="button"
    >
      <svg 
        className="w-4 h-4" 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={2} 
          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" 
        />
      </svg>
    </button>
  );

  const totalRevenue = (() => {
    let total = (formData.feeBasedRevenue || 0) + (formData.commissionRevenue || 0);
    
    // Add conditional revenue fields
    if (!partialSalesPropertyCasualty) {
      total += (formData.trailerRevenue || 0);
      if (company && !company.feature_types?.includes('hide_direct_revenue')) {
        total += (formData.directRevenue || 0);
      }
    }
    
    return total;
  })();

  const getMultipleOfRevenue = () => {
    if (totalRevenue === 0) return 0;
    return (formData.askingPrice || 0) / totalRevenue;
  };

  const formatCurrency = (amount) => `${currencySymbol()}${numeral(amount).format('0,0')}`;
  const formatNumber = (number) => numeral(number).format('0,0');

  const cardClasses = "bg-white border rounded-lg p-6 space-y-4";
  const sectionTitleClasses = "text-xl font-semibold mb-4";
  const itemClasses = "flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0";
  const labelClasses = "text-sm font-medium text-gray-600";
  const valueClasses = "text-sm font-semibold";

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-4 text-succession-text-primary transition-colors duration-300">
          <FormattedMessage id="succession.partial_sales.review.title" />
        </h2>
        <p className="text-lg mb-6 text-succession-text-secondary transition-colors duration-300">
          <FormattedMessage id="succession.partial_sales.review.intro" />
        </p>
      </div>

      {/* Segment Overview */}
      <div className={`${cardClasses} border-succession-border transition-colors duration-300`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`${sectionTitleClasses} text-succession-text-primary transition-colors duration-300 mb-0`}>
            <FormattedMessage id="succession.partial_sales.review.segment_overview" />
          </h3>
          <EditButton onClick={() => goToStep(STEP.DESCRIPTION)} label={intl.formatMessage({ id: 'succession.partial_sales.review.segment_overview' })} />
        </div>
        <div className="space-y-3">
          <div className={itemClasses}>
            <span className={labelClasses}><FormattedMessage id="succession.partial_sales.review.segment_name_label" /></span>
            <span className={`${valueClasses} text-succession-text-primary transition-colors duration-300`}>
              {formData.segmentDetails || intl.formatMessage({ id: 'succession.partial_sales.review.not_specified' })}
            </span>
          </div>
          <div className="py-2">
            <span className={labelClasses}><FormattedMessage id="succession.partial_sales.review.description_label" /></span>
            <p className="mt-1 text-sm text-succession-text-primary transition-colors duration-300">
              {formData.description || intl.formatMessage({ id: 'succession.partial_sales.review.no_description' })}
            </p>
          </div>
        </div>
      </div>

      {/* Financial Details */}
      <div className={`${cardClasses} border-succession-border transition-colors duration-300`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`${sectionTitleClasses} text-succession-text-primary transition-colors duration-300 mb-0`}>
            <FormattedMessage id="succession.partial_sales.review.financial_details" />
          </h3>
          <EditButton onClick={() => goToStep(STEP.FINANCIAL_DETAILS)} label={intl.formatMessage({ id: 'succession.partial_sales.review.financial_details' })} />
        </div>
        <div className="space-y-3">
          <div className={itemClasses}>
            <span className={labelClasses}><FormattedMessage id="succession.partial_sales.review.number_of_clients_label" /></span>
            <span className={`${valueClasses} text-succession-text-primary transition-colors duration-300`}>
              {formatNumber(formData.numberOfClients)}
            </span>
          </div>
          <div className={itemClasses}>
            <span className={labelClasses}>
              {partialSalesPropertyCasualty ? <FormattedMessage id="succession.partial_sales.review.total_premium_label" /> : <FormattedMessage id="succession.partial_sales.review.aum_label" />}
            </span>
            <span className={`${valueClasses} text-succession-text-primary transition-colors duration-300`}>
              {formatCurrency(formData.assetsUnderManagement)}
            </span>
          </div>
          <div className={itemClasses}>
            <span className={labelClasses}>
              {partialSalesPropertyCasualty ? <FormattedMessage id="succession.partial_sales.review.recurring_commission_label" /> : <FormattedMessage id="succession.partial_sales.review.fee_based_revenue_label" />}
            </span>
            <span className={`${valueClasses} text-succession-text-primary transition-colors duration-300`}>
              {formatCurrency(formData.feeBasedRevenue)}
            </span>
          </div>
          {!partialSalesPropertyCasualty && (
            <div className={itemClasses}>
              <span className={labelClasses}><FormattedMessage id="succession.partial_sales.review.trails_revenue_label" /></span>
              <span className={`${valueClasses} text-succession-text-primary transition-colors duration-300`}>
                {formatCurrency(formData.trailerRevenue)}
              </span>
            </div>
          )}
          {!partialSalesPropertyCasualty && (company && !company.feature_types?.includes('hide_direct_revenue')) && (
            <div className={itemClasses}>
              <span className={labelClasses}><FormattedMessage id="succession.partial_sales.review.direct_revenue_label" /></span>
              <span className={`${valueClasses} text-succession-text-primary transition-colors duration-300`}>
                {formatCurrency(formData.directRevenue)}
              </span>
            </div>
          )}
          <div className={itemClasses}>
            <span className={labelClasses}>
              {partialSalesPropertyCasualty ? <FormattedMessage id="succession.partial_sales.review.new_commission_label" /> : <FormattedMessage id="succession.partial_sales.review.commission_revenue_label" />}
            </span>
            <span className={`${valueClasses} text-succession-text-primary transition-colors duration-300`}>
              {formatCurrency(formData.commissionRevenue)}
            </span>
          </div>
          <div className="pt-3 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold text-succession-text-primary transition-colors duration-300">
                <FormattedMessage id="succession.partial_sales.review.total_annual_revenue" />
              </span>
              <span className="text-xl font-bold text-succession-success">
                {formatCurrency(totalRevenue)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Geographic Coverage */}
      <div className={`${cardClasses} border-succession-border transition-colors duration-300`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`${sectionTitleClasses} text-succession-text-primary transition-colors duration-300 mb-0`}>
            <FormattedMessage id="succession.partial_sales.review.geographic_coverage" />
          </h3>
          <EditButton onClick={() => goToStep(STEP.DETAILS)} label={intl.formatMessage({ id: 'succession.partial_sales.review.geographic_coverage' })} />
        </div>
        <div className="py-2">
          <span className={labelClasses}>
            {company?.country?.toUpperCase() === 'US' ? <FormattedMessage id="succession.partial_sales.review.states_label" /> : <FormattedMessage id="succession.partial_sales.review.provinces_label" />}
          </span>
          <p className="mt-1 text-sm text-succession-text-primary transition-colors duration-300">
            {formData.states?.join(', ') || intl.formatMessage({ id: 'succession.partial_sales.review.none_specified' })}
          </p>
        </div>
      </div>

      {/* Additional Details */}
      {(formData.clientDemographicsDetails || formData.servicesProvidedDetails || formData.otherDetails) && (
        <div className={`${cardClasses} border-succession-border transition-colors duration-300`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`${sectionTitleClasses} text-succession-text-primary transition-colors duration-300 mb-0`}>
              <FormattedMessage id="succession.partial_sales.review.additional_details" />
            </h3>
            <EditButton onClick={() => goToStep(STEP.DETAILS)} label={intl.formatMessage({ id: 'succession.partial_sales.review.additional_details' })} />
          </div>
          <div className="space-y-4">
            {formData.clientDemographicsDetails && (
              <div>
                <span className={labelClasses}><FormattedMessage id="succession.partial_sales.review.client_demographics_label" /></span>
                <p className="mt-1 text-sm text-succession-text-primary transition-colors duration-300">
                  {formData.clientDemographicsDetails}
                </p>
              </div>
            )}
            {formData.servicesProvidedDetails && (
              <div>
                <span className={labelClasses}><FormattedMessage id="succession.partial_sales.review.services_provided_label" /></span>
                <p className="mt-1 text-sm text-succession-text-primary transition-colors duration-300">
                  {formData.servicesProvidedDetails}
                </p>
              </div>
            )}
            {formData.otherDetails && (
              <div>
                <span className={labelClasses}><FormattedMessage id="succession.partial_sales.review.other_details_label" /></span>
                <p className="mt-1 text-sm text-succession-text-primary transition-colors duration-300">
                  {formData.otherDetails}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Questions and Answers */}
      {partialSaleQuestions && partialSaleQuestions.length > 0 && (
        <div className={`${cardClasses} border-succession-border transition-colors duration-300`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`${sectionTitleClasses} text-succession-text-primary transition-colors duration-300 mb-0`}>
              <FormattedMessage id="succession.partial_sales.review.questionnaire_responses" />
            </h3>
            <EditButton onClick={() => goToStep(STEP.DETAILS)} label={intl.formatMessage({ id: 'succession.partial_sales.review.questionnaire_responses' })} />
          </div>
          <div className="space-y-3">
            {partialSaleQuestions.map((question, index) => (
              <div key={question.id || index} className={itemClasses}>
                <span className={labelClasses}>{question.question}</span>
                <span className={`${valueClasses} ${formData.answers[index] ? 'text-succession-success' : 'text-succession-error'}`}>
                  {formData.answers[index] ? intl.formatMessage({ id: 'succession.partial_sales.review.yes' }) : intl.formatMessage({ id: 'succession.partial_sales.review.no' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pricing Details */}
      {!hideProductSalesPurchasePrice && (
        <div className={`${cardClasses} border-succession-border transition-colors duration-300`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`${sectionTitleClasses} text-succession-text-primary transition-colors duration-300 mb-0`}>
              <FormattedMessage id="succession.partial_sales.review.pricing_details" />
            </h3>
            <EditButton onClick={() => goToStep(STEP.PRICE_DETAILS)} label={intl.formatMessage({ id: 'succession.partial_sales.review.pricing_details' })} />
          </div>
          <div className="space-y-3">
            <div className={itemClasses}>
              <span className={labelClasses}><FormattedMessage id="succession.partial_sales.review.asking_price_label" /></span>
              <span className="text-xl font-bold text-succession-success">
                {!formData.askingPrice || formData.askingPrice === 0 || parseFloat(formData.askingPrice) === 0
                  ? intl.formatMessage({ id: 'succession.partial_sales.review.negotiable' })
                  : formatCurrency(formData.askingPrice)
                }
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Important Notes */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-yellow-900 mb-2">
          <FormattedMessage id="succession.partial_sales.review.important_notes_title" />
        </h3>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• <FormattedMessage id="succession.partial_sales.review.note_1" /></li>
          <li>• <FormattedMessage id="succession.partial_sales.review.note_2" /></li>
          <li>• <FormattedMessage id="succession.partial_sales.review.note_3" /></li>
          <li>• <FormattedMessage id="succession.partial_sales.review.note_4" /></li>
        </ul>
      </div>
    </div>
  );
};

export default Review;